import React, { useState } from 'react';
import { IconUserPlus, IconCircleCheck, IconCopy, IconCheck, IconMail } from '@tabler/icons-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../auth/firebaseConfig';
import Modal from '../common/Modal';

const functions = getFunctions(app, 'us-east1');
const sendSiteInviteFn = httpsCallable(functions, 'ubuSendSiteInvite');

// Both paths land on the same place: the signup page. There's nothing to
// accept and no group to join, so the link needs no token and never expires.
const INVITE_URL = 'https://ubunited.us/auth?mode=signup';

const SiteInviteModal = ({ onClose }) => {
  const [emailsRaw, setEmailsRaw] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(INVITE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      // The Clipboard API needs a secure context and can be blocked outright;
      // selecting the text is the reliable fallback.
      setError('Could not copy automatically — select the link and copy it.');
    }
  };

  const handleSend = async () => {
    setError('');
    const emails = emailsRaw.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    if (emails.length === 0) { setError('Enter at least one email address.'); return; }
    setSending(true);
    try {
      const res = await sendSiteInviteFn({ emails, note });
      setResult(res.data);
    } catch (err) {
      setError(err.message || 'Could not send those invites.');
    } finally {
      setSending(false);
    }
  };

  if (result) {
    return (
      <Modal title="Invites Sent" icon={IconCircleCheck} size="sm" onClose={onClose}
        footer={<button className="uu-submit-btn" onClick={onClose}>Done</button>}
      >
        <p className="uu-modal2-text">
          {result.sent} invite{result.sent !== 1 ? 's' : ''} on the way.
        </p>
        {result.failed?.length > 0 && (
          <p className="uu-modal2-subtext">
            Couldn't send to: {result.failed.join(', ')}
          </p>
        )}
        {result.notSent?.length > 0 && (
          <p className="uu-modal2-subtext">
            Not sent today (daily invite limit reached): {result.notSent.join(', ')}
          </p>
        )}
        <p className="uu-modal2-subtext">
          You can send {result.remainingToday} more invite
          {result.remainingToday !== 1 ? 's' : ''} today.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Send an Invite"
      icon={IconUserPlus}
      onClose={onClose}
      footer={
        <>
          <button className="uu-modal2-cancel" onClick={onClose}>Close</button>
          <button className="uu-submit-btn" onClick={handleSend} disabled={sending}>
            <IconMail size={15} stroke={2} />
            {sending ? 'Sending...' : 'Send Invites'}
          </button>
        </>
      }
    >
      <p className="uu-modal2-text">
        Invite someone to UBunited. Either way they land on the signup page —
        nothing to accept, no expiry.
      </p>

      <fieldset className="uu-form-group">
        <label htmlFor="si-emails"><IconMail size={14} stroke={2} /> Invite by email</label>
        <textarea
          id="si-emails" className="uu-form-textarea" rows={3}
          value={emailsRaw} onChange={(e) => setEmailsRaw(e.target.value)}
          placeholder="one@example.com, two@example.com"
        />
        <span className="uu-field-hint">Separate with commas or new lines — up to 10 at a time.</span>
      </fieldset>

      <fieldset className="uu-form-group">
        <label htmlFor="si-note">Add a note (optional)</label>
        <textarea
          id="si-note" className="uu-form-textarea" rows={2}
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Come play with us on Tuesdays!"
        />
      </fieldset>

      <fieldset className="uu-form-group">
        <label htmlFor="si-link">Or share a link</label>
        <section className="uu-si-link-row">
          <input
            id="si-link" type="text" className="uu-form-input uu-si-link"
            value={INVITE_URL} readOnly
            onFocus={(e) => e.target.select()}
          />
          <button type="button" className="uu-si-copy-btn" onClick={handleCopy}>
            {copied
              ? <><IconCheck size={15} stroke={2} /> Copied</>
              : <><IconCopy size={15} stroke={2} /> Copy</>}
          </button>
        </section>
        <span className="uu-field-hint">
          Paste it anywhere — text, WhatsApp, a group chat.
        </span>
      </fieldset>

      {error && <p className="uu-modal2-error">{error}</p>}
    </Modal>
  );
};

export default SiteInviteModal;
